import React, { useState } from 'react';
import { useNavigate, Link, Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';

const LoginPage: React.FC = () => {
  const { login, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  
  // Form state
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetLoading, setResetLoading] = useState(false);

  // Redirect to dashboard if already logged in
  if (isAuthenticated) {
    return <Navigate to="/admin/dashboard" replace />;
  }
  
  // Handle login form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email || !password) {
      setError('Please enter both email and password');
      return;
    }
    
    try {
      setError('');
      setSuccess('');
      setLoading(true);
      
      // Log in with provided credentials
      await login(email, password);
      
      // Redirect to admin dashboard on success
      window.location.href = '/admin/dashboard';
    } catch (err: any) {
      setError(
        err.response?.data?.error || 
        'Login failed. Please check your credentials.'
      );
    } finally {
      setLoading(false);
    }
  };

  // Handle forgot password form submission
  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!resetEmail) {
      setError('Please enter your email address');
      return;
    }
    
    try {
      setError('');
      setSuccess('');
      setResetLoading(true);
      
      const response = await axios.post('/api/auth/forgotpassword', { email: resetEmail });
      
      if (response.data.success) {
        setSuccess('Password reset email sent! Please check your inbox.');
        setResetEmail('');
        // Don't automatically hide the form to allow the user to see the success message
      } else {
        setError('Failed to send reset email');
      }
    } catch (err: any) {
      setError(
        err.response?.data?.error || 
        'Failed to process your request. Please try again.'
      );
    } finally {
      setResetLoading(false);
    }
  };

  // Toggle between login and forgot password forms
  const toggleForgotPassword = () => {
    setShowForgotPassword(!showForgotPassword);
    setError('');
    setSuccess('');
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background dark:bg-dark-background py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8 bg-card dark:bg-dark-card p-8 rounded-lg shadow-lg">
        <div>
          <Link to="/" className="flex justify-center">
            <h1 className="text-2xl font-bold text-primary dark:text-white">ProCreative</h1>
          </Link>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-foreground dark:text-dark-foreground">
            {showForgotPassword ? 'Reset Password' : 'Admin Login'}
          </h2>
        </div>
        
        {error && (
          <div className="bg-destructive/10 dark:bg-dark-destructive/20 text-destructive dark:text-dark-destructive p-3 rounded-md">
            {error}
          </div>
        )}
        
        {success && (
          <div className="bg-success/10 dark:bg-dark-success/20 text-success dark:text-white font-medium p-3 rounded-md">
            {success}
          </div>
        )}
        
        {!showForgotPassword ? (
          // Login Form
          <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
            <div className="rounded-md -space-y-px">
              <div className="mb-4">
                <label htmlFor="email-address" className="block mb-1 text-sm font-medium text-foreground dark:text-dark-foreground">
                  Email address
                </label>
                <input
                  id="email-address"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="appearance-none relative block w-full px-3 py-2 border border-input dark:border-dark-input rounded-md focus:outline-none focus:ring-primary dark:focus:ring-dark-primary focus:border-primary dark:focus:border-dark-primary sm:text-sm bg-background dark:bg-dark-background text-foreground dark:text-dark-foreground"
                  placeholder="Email address"
                />
              </div>
              <div>
                <label htmlFor="password" className="block mb-1 text-sm font-medium text-foreground dark:text-dark-foreground">
                  Password
                </label>
                <input
                  id="password"
                  name="password"
                  type="password"
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="appearance-none relative block w-full px-3 py-2 border border-input dark:border-dark-input rounded-md focus:outline-none focus:ring-primary dark:focus:ring-dark-primary focus:border-primary dark:focus:border-dark-primary sm:text-sm bg-background dark:bg-dark-background text-foreground dark:text-dark-foreground"
                  placeholder="Password"
                />
              </div>
            </div>

            <div>
              <button
                type="submit"
                disabled={loading}
                className="group relative w-full flex justify-center py-2 px-4 border border-transparent rounded-md text-white bg-primary hover:bg-primary/90 dark:bg-dark-primary dark:hover:bg-dark-primary/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary dark:focus:ring-dark-primary disabled:opacity-50"
              >
                {loading ? 'Signing in...' : 'Sign in'}
              </button>
            </div>
            
            <div className="flex items-center justify-between mt-4 text-sm">
              <button 
                type="button" 
                onClick={toggleForgotPassword}
                className="text-primary hover:text-primary/80 dark:text-white dark:hover:text-white/80"
              >
                Forgot your password?
              </button>
              
              <Link to="/" className="text-primary hover:text-primary/80 dark:text-white dark:hover:text-white/80">
                Return to home
              </Link>
            </div>
          </form>
        ) : (
          // Forgot Password Form
          <form className="mt-8 space-y-6" onSubmit={handleForgotPassword}>
            <div className="mb-4">
              <label htmlFor="reset-email" className="block mb-1 text-sm font-medium text-foreground dark:text-dark-foreground">
                Email address
              </label>
              <input
                id="reset-email"
                name="email"
                type="email"
                autoComplete="email"
                required
                value={resetEmail}
                onChange={(e) => setResetEmail(e.target.value)}
                className="appearance-none relative block w-full px-3 py-2 border border-input dark:border-dark-input rounded-md focus:outline-none focus:ring-primary dark:focus:ring-dark-primary focus:border-primary dark:focus:border-dark-primary sm:text-sm bg-background dark:bg-dark-background text-foreground dark:text-dark-foreground"
                placeholder="Enter your email address"
              />
              <p className="mt-2 text-xs text-muted-foreground dark:text-dark-muted-foreground">
                We'll send you an email with password reset instructions.
              </p>
            </div>

            <div>
              <button
                type="submit"
                disabled={resetLoading}
                className="group relative w-full flex justify-center py-2 px-4 border border-transparent rounded-md text-white bg-primary hover:bg-primary/90 dark:bg-dark-primary dark:hover:bg-dark-primary/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary dark:focus:ring-dark-primary disabled:opacity-50"
              >
                {resetLoading ? 'Sending...' : 'Send Reset Link'}
              </button>
            </div>
            
            <div className="text-center mt-4">
              <button 
                type="button" 
                onClick={toggleForgotPassword}
                className="text-primary hover:text-primary/80 dark:text-white dark:hover:text-white/80"
              >
                Back to login
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default LoginPage; 